"use client";

import { useState, useEffect } from "react";

import { cn } from "@/lib/utils";

type PricingData = {
  monthly: { amount: number; currency: string; originalPrice: number | null };
  yearly: { amount: number; currency: string; originalPrice: number | null };
} | null;

function formatEur(cents: number): string {
  return `${(cents / 100).toFixed(0).replace(".", ",")} €`;
}

export function PlanSelector({
  onSelect,
  defaultPlan = "yearly",
}: {
  onSelect: (plan: "monthly" | "yearly") => void;
  defaultPlan?: "monthly" | "yearly";
}) {
  const [pricing, setPricing] = useState<PricingData>(null);
  const [selected, setSelected] = useState<"monthly" | "yearly">(defaultPlan);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch("/api/pricing")
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setPricing)
      .catch(() => setError(true));
  }, []);

  const handleSelect = (plan: "monthly" | "yearly") => {
    setSelected(plan);
    onSelect(plan);
  };

  if (error) {
    return (
      <p className="text-copy-sm text-red-600 text-center py-2">
        Preise konnten nicht geladen werden. Bitte lade die Seite neu.
      </p>
    );
  }

  if (!pricing) {
    return (
      <div className="flex flex-col gap-3 w-full animate-pulse">
        <div className="h-20 bg-sand-50 rounded-2xl" />
        <div className="h-20 bg-sand-50 rounded-2xl" />
      </div>
    );
  }

  const plans = [
    {
      key: "monthly" as const,
      label: "Monatlich",
      amount: pricing.monthly.amount,
      period: "pro Monat",
    },
    {
      key: "yearly" as const,
      label: "Jährlich",
      amount: pricing.yearly.amount,
      period: "pro Jahr",
    },
  ];

  return (
    <div className="flex flex-col gap-3 w-full">
      {plans.map((plan) => {
        const originalPrice = plan.key === "monthly"
          ? pricing.monthly.originalPrice
          : pricing.yearly.originalPrice;
        const hasDiscount = originalPrice && plan.amount < originalPrice;

        return (
          <button
            key={plan.key}
            type="button"
            onClick={() => handleSelect(plan.key)}
            className={cn(
              "relative w-full rounded-2xl p-4 text-left transition-all",
              selected === plan.key
                ? "bg-white ring-2 ring-sky-100"
                : "bg-white ring-1 ring-gray-25"
            )}
          >
            <p className="text-copy-bold text-black">{plan.label}</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-headline-md text-black">
                {formatEur(plan.amount)}
              </span>
              {hasDiscount && (
                <span className="text-copy-sm text-gray-75 line-through">
                  {formatEur(originalPrice)}
                </span>
              )}
              <span className="text-copy-sm text-gray-75">{plan.period}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
